import dayjs from "dayjs";
import { uniq } from "lodash";
import { insideCircle } from "geolocation-utils";

import { api } from "api";
import { Utils } from "utils";
import { Road } from "services/road";
// import { Hotels } from "services/hotels";
import { SameRoads } from "services/same-roads";
import { Iterable, IPlace, IRouteDay } from "types";
import { BestBikingRoad } from "services/best-biking-road";
import {
  NIGHT_BREAKTIME_IN_MINUTES,
  START_OF_THE_DAY_BREAKTIME_IN_MINUTES,
} from "constants/constants";

type SameRoadsResponse = ReturnType<typeof SameRoads.prototype.findSameRoads>;
export class RouteDays {
  private _sameRoads: SameRoadsResponse = { indexes: [], coordinates: [] };
  public bestBikingRoads: BestBikingRoad[] = [];

  constructor(
    private days: IRouteDay[],
    private allPlaces: Iterable<IPlace>[]
  ) {
    this.days = days;
    this.allPlaces = allPlaces;
  }

  /* Public Setters */

  public set sameRoads(roads: SameRoadsResponse) {
    this._sameRoads = { ...roads };
  }

  /* Public Methods */

  public addAdditionalInfo() {
    this.addSuggestedPlaces();
    this.addBreakTimes();
    this.defineSameRoads();
    this.addBestBikingRoads();
  }

  public async addHotelsAndCampings() {
    // await this.addHotels();
    await this.addCampings();
  }

  /* Public Getters */

  public get data() {
    return this.days;
  }

  /* Private Getters */

  private get selectedPlaces() {
    return this.days
      .map((day) => day.selectedPlaces.filter(Boolean) as Iterable<IPlace>[])
      .flat(1);
  }

  /* Private Methods */

  private addSuggestedPlaces() {
    const updated: typeof this.days = [];

    /* Get all occupied places ids (blacklist) and fill while suggesting  */
    let occupiedIds = this.selectedPlaces.map((item) => item.id);

    for (const [index, day] of this.days.entries()) {
      const places = day.selectedPlaces;
      const suggested: Iterable<IPlace>[][] = [];

      /* Loop through each selected place */
      for (const place of places) {
        if (!place) {
          suggested.push([]);
          continue;
        }

        const radius = 20 * 1000; // 20km
        const center = { lat: place.latitude, lng: place.longitude };

        const occupiedSuggested = occupiedIds;
        const currentPlaceSuggested: Iterable<IPlace>[] = [];

        /* Find places inside radius and not occupied */

        this.allPlaces.forEach((item) => {
          const location = { lat: item.latitude, lng: item.longitude };
          const isInsideCircle = insideCircle(location, center, radius);

          const date = dayjs().add(index, "days");
          const isAvailbleForDate = Utils.Places.availableForDate(date, item);
          const isOccupied = occupiedSuggested.includes(item.id);

          if (!isOccupied && isAvailbleForDate && isInsideCircle) {
            occupiedSuggested.push(item.id);
            currentPlaceSuggested.push(item);
          }
        });

        occupiedIds = uniq([...occupiedSuggested]);
        suggested.push(currentPlaceSuggested);
      }

      updated.push({ ...day, suggestedPlaces: suggested });
    }

    this.days = updated;
  }

  private addBreakTimes() {
    const updated: typeof this.days = [];

    for (const day of this.days) {
      /* Set Start of the day / Night Breaktime */

      const updatedSelectedPlaces = day.selectedPlaces.map((item, index) => {
        let breaktime = item?.breaktime ?? 0;

        if (index === 0) {
          breaktime = START_OF_THE_DAY_BREAKTIME_IN_MINUTES;
        } else if (index === day.selectedPlaces.length - 1)
          breaktime = NIGHT_BREAKTIME_IN_MINUTES;

        return item ? { ...item, breaktime } : item;
      });

      updated.push({
        ...day,
        selectedPlaces: updatedSelectedPlaces,
      });
    }

    this.days = updated;
  }

  // For now, we fetching hotels on each day independantly
  // Check getHotelsThunk()

  /* 
  private async addHotels() {
    const updated: typeof this.days = [];

    for (const day of this.days) {
      const places = day.selectedPlaces;
      const lastItem = places.at(places.length - 1);

      if (lastItem && lastItem.address_info.country_code) {
        const code = lastItem.address_info.country_code;
        const countryName = Utils.Helpers.getCountryName(code);

        const hotels = new Hotels(
          countryName,
          lastItem.address_info.city ?? lastItem.title
        );
        await hotels.search();

        updated.push({ ...day, hotels: hotels.list });
        continue;
      }

      updated.push(day);
    }

    this.days = updated;
  }
  */

  private async addCampings() {
    const updated: typeof this.days = [];

    const countryCodes = this.selectedPlaces.map(
      (i) => i.address_info.country_code
    );
    const campings = await api.firestore.routes.places(uniq(countryCodes), [
      "Campings",
    ]);

    for (const day of this.days) {
      const waypoints = day.selectedWaypoints;
      const center = waypoints[waypoints.length - 1];
      const radius = 15 * 1000; // 15km

      const filtered = campings.filter((camping) => {
        const location = { lat: camping.latitude, lng: camping.longitude };
        return insideCircle(location, center, radius);
      });

      updated.push({ ...day, campings: filtered });
    }

    this.days = updated;
  }

  private defineSameRoads() {
    const updated: typeof this.days = [];

    for (const day of this.days) {
      const road = Road.fromPoints(day.points);
      const coords = road.coordinates;
      const roadCoordsSet = new Set(coords.map((i) => JSON.stringify(i)));

      const filtered = this._sameRoads.coordinates
        .map((coords) => {
          return coords.filter((coord) =>
            roadCoordsSet.has(JSON.stringify(coord))
          );
        })
        .filter((coords) => coords.length > 0);

      updated.push({ ...day, sameRoads: filtered });
    }

    this.days = updated;
  }

  private addBestBikingRoads() {
    this.days = this.days.map((item, index) => {
      const selected = this.bestBikingRoads.filter((bestBikingRoad) => {
        const road = Road.fromPoints(item.points);
        return (
          bestBikingRoad.coordinates.filter((coord) => {
            return road.isInBoundingBox(coord);
          }).length > 0
        );
      });

      return {
        ...item,
        bestBikingRoads: selected.map((item) => item.coordinates),
      };
    });
  }
}
